import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input, DatePicker, Skeleton, Empty, Table, Tooltip } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  CalendarOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { TeamOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../../config";
import EnquiryFollowUp from "./EnquiryFollowUp";
import {
  useLeadDetailsFollowUp,
  useFollowUpOptions,
  useMyTeam,
  useAllocateLeads,
  useImportEnquiries,
} from "../hooks/useManageEnquiry";
import { AdditionalPhones, withCountryCode } from "../utils/phones";
import { currentUserId } from "../utils/role";

const PAGE_SIZE = 10;

/* Colours keyed by lead_follow_up_status option_value — the LABEL always comes
   from the options master, never from a hardcoded list here. */
const STATUS_STYLE = {
  "0":  { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  "1":  { bg: "#fff1f2", color: "#e11d48", border: "#fecdd3" },
  "2":  { bg: "#f5f5f5", color: "#6b7280", border: "#d1d5db" },
  "3":  { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  "4":  { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  "5":  { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  "6":  { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  "7":  { bg: "#ecfdf5", color: "#059669", border: "#6ee7b7" },
  "8":  { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  "9":  { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  "10": { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

const EMPTY_FILTER = { search: "", start_date: null, end_date: null };

/* Same labels and colours as the Kanban board, so a stage reads identically
   wherever it is shown. */
const STAGE_LABELS = {
  new:       { label: "New",       color: "#6366f1", border: "#c7d2fe", bg: "#eef2ff" },
  contacted: { label: "Contacted", color: "#0891b2", border: "#bae6fd", bg: "#e0f2fe" },
  future:    { label: "Future",    color: "#d97706", border: "#fde68a", bg: "#fffbeb" },
  done:      { label: "Done",      color: "#16a34a", border: "#86efac", bg: "#dcfce7" },
  negative:  { label: "Negative",  color: "#dc2626", border: "#fca5a5", bg: "#fee2e2" },
};

const thStyle = {
  fontSize: "12px", fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};

export default function UnassignedLeads() {
  const [page, setPage]                       = useState(1);
  const [filter, setFilter]                   = useState({ ...EMPTY_FILTER });
  const [appliedFilter, setAppliedFilter]     = useState({ ...EMPTY_FILTER });
  const [filtersOpen, setFiltersOpen]         = useState(true);
  const [selectedLeadId, setSelectedLeadId]   = useState(null);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);
  const debounceRef = useRef(null);

  /* ── Allocate ── */
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin]         = useState(null);
  const [selectedIds, setSelectedIds]             = useState([]);
  /* Allocation targets are limited to the logged-in user's own team (self +
     reportees) — a manager must not be able to hand a lead to someone outside
     their reporting line. Super-admin / admin still get every internal user. */
  const { data: internalUsers = [] } = useMyTeam();
  const allocateLeads = useAllocateLeads();

  /* Import lives here too: imported leads arrive unassigned, so this is the list
     they actually land in — uploading from the page where you then allocate them
     saves a hop. */
  const importMutation = useImportEnquiries();
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const handleFileChange = (e) => { setFile(e.target.files[0]); setFileError(""); };
  const handleImport = () => {
    if (!file) { setFileError("File is required"); return; }
    importMutation.mutate(file, {
      onSuccess: () => { setImportOpen(false); setFile(null); setPage(1); },
    });
  };

  /* Self-assign is limited to leads this user created. The shared pool stays with
     the auto-assign cron / a manager — otherwise everyone would cherry-pick the
     good leads and the lead_limit distribution would mean nothing. */
  const isMyOwnLead = (row) =>
    row?.created_by != null && String(row.created_by) === String(currentUserId());

  const handleAssignToMe = (row) => {
    Swal.fire({
      title: "Assign this lead to you?",
      html: `<b>${row.name || "This lead"}</b> was created by you, so you can take it into your Inbox.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, assign to me",
      confirmButtonColor: "#d97706",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      allocateLeads.mutate(
        { adminId: currentUserId(), leadIds: [row.id] },
        {
          onSuccess: () =>
            Swal.fire({ icon: "success", title: "Assigned", text: "The lead is now in your Inbox.", confirmButtonColor: "#d97706" }),
          onError: (err) => Swal.fire("Error", err?.message || "Could not assign this lead.", "error"),
        },
      );
    });
  };

  /* ── search debounce ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilter((p) => ({ ...p, search: filter.search }));
      setPage(1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter.search]);

  /* ── data: leads nobody owns yet ── */
  const { data, isFetching, isError } = useQuery({
    queryKey: ["unassigned-leads", page, appliedFilter],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` },
        params: {
          page,
          limit: PAGE_SIZE,
          is_assigned: 0, // the whole point of this screen
          search: appliedFilter.search || "",
          start_date: appliedFilter.start_date || "",
          end_date: appliedFilter.end_date || "",
        },
      });
      const rows = res.data?.data || [];
      // Belt and braces: keep only rows with no owner, in case the API ignores
      // is_assigned=0 (it is a newer param).
      const unassigned = rows.filter((r) => !r.assign_to);
      return {
        data: unassigned,
        total: res.data?.total_records ?? res.data?.pagination?.total_records ?? unassigned.length,
        totalPages:
          res.data?.pagination?.total_pages ??
          (Math.ceil(unassigned.length / PAGE_SIZE) || 1),
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => { if (isError) toast.error("Failed to load unassigned leads"); }, [isError]);

  /* Infinite scroll: each page is appended as the user reaches the bottom, so
     there is no page bar. Merge by id — allocating leads shifts the pages, and a
     lead must not show up twice. */
  const [listAccum, setListAccum] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const pageRows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  useEffect(() => {
    if (!data) return;
    setListAccum((prev) => {
      if (page === 1) return pageRows;
      const byId = new Map(prev.map((i) => [i.id, i]));
      pageRows.forEach((i) => byId.set(i.id, i));
      return Array.from(byId.values());
    });
    setHasMore(page < totalPages);
    setIsLoadingMore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isFetching || isLoadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsLoadingMore(true);
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, isFetching, isLoadingMore]);

  const leads = listAccum;

  /* ── status labels from the options master ── */
  const { data: fuOptsRaw = [] } = useFollowUpOptions();
  const fuOpts = Array.isArray(fuOptsRaw) ? fuOptsRaw : (fuOptsRaw?.data || []);
  const fuLabelMap = Object.fromEntries(fuOpts.map((o) => [String(o.option_value), o.label]));

  /* ── open the lead's activity panel ── */
  const { data: leadDetails } = useLeadDetailsFollowUp(selectedLeadId);
  useEffect(() => {
    if (!leadDetails) return;
    setFollowUpEnquiry({
      id:      leadDetails.id,
      name:    leadDetails.name,
      email:   leadDetails.email,
      phone:   leadDetails.phone,
      additional_phone:   leadDetails.additional_phone || "",
      company: leadDetails.company,
      address: leadDetails.address,
      message: leadDetails.message,
      status:
        leadDetails.is_positive === 1 ? "Positive"
        : leadDetails.is_positive === 2 ? "Negative"
        : "Neutral",
      assignToId:     leadDetails.assign_to      || null,
      assignedToName: leadDetails.assign_to_name || "Unassigned",
      stage:          leadDetails.stage_name,
      followUps:      leadDetails.follow_ups     || [],
      // Attribution is carried through to Add Customer when the lead
      // converts, so it must survive this mapping.
      utm_source:    leadDetails.utm_source    || "",
      utm_medium:    leadDetails.utm_medium    || "",
      utm_campaign:  leadDetails.utm_campaign  || "",
    });
  }, [leadDetails]);

  /* Back button inside the panel closes it */
  useEffect(() => {
    const onPop = () => { setFollowUpEnquiry(null); setSelectedLeadId(null); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Attorney column only when the page actually has attorney numbers
  const showAttorneyCol = leads.some((l) => String(l?.attorney_number ?? "").trim());

  const columns = [
    {
      title: <span style={thStyle}>#</span>,
      key: "index", width: 50, align: "center",
      render: (_, __, idx) => (
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}>
          {(page - 1) * PAGE_SIZE + idx + 1}
        </span>
      ),
    },
    {
      title: <span style={thStyle}>Lead Name</span>,
      key: "name",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(row.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div
              onClick={() => setSelectedLeadId(row.id)}
              style={{ fontWeight: 700, color: "#d97706", cursor: "pointer", fontSize: 14 }}
            >
              {row.name || "—"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {row.company && <span style={{ fontSize: 11, color: "#9ca3af" }}>{row.company}</span>}
              {row.demo_date && (
                <span
                  title="Demo scheduled for this lead"
                  style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "0 7px", fontSize: 10, fontWeight: 700 }}
                >
                  📅 Demo {dayjs(row.demo_date).format("DD-MM-YYYY HH:mm")}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <span style={thStyle}>Email</span>,
      dataIndex: "email", key: "email",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Phone</span>,
      key: "phone",
      // Primary (green dot) + alternate number (blue dot) when the lead has one
      render: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {row.phone ? (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#374151", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
              {withCountryCode(row.country_code, row.phone)}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
          )}
          <AdditionalPhones
            value={row.additional_phone}
            dot
            max={2}
            itemStyle={{ fontSize: 12 }}
          />
        </div>
      ),
    },
    ...(showAttorneyCol
      ? [{
          title: <span style={thStyle}>Attorney No.</span>,
          dataIndex: "attorney_number", key: "attorney_number", align: "center",
          render: (v) =>
            String(v ?? "").trim() ? (
              <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 6, padding: "2px 9px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                {v}
              </span>
            ) : (
              <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
            ),
        }]
      : []),
    {
      title: <span style={thStyle}>Lead Status</span>,
      key: "lead_status",
      /* Stage from the API's `kanban_column` — the single source of truth used by
         the board and the Inbox list. A rep's own marking (Hot Lead, Interested…)
         is shown only when there is one; a lead created with the default status
         would otherwise claim "Follow Up" while it is plainly New. */
      render: (_, row) => {
        const val = String(row?.status ?? "");
        const hasMarking = val !== "" && val !== "0" && !!fuLabelMap[val];
        if (hasMarking) {
          const s = STATUS_STYLE[val] || STATUS_STYLE["0"];
          return (
            <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
              {fuLabelMap[val]}
            </span>
          );
        }
        const stage = STAGE_LABELS[String(row?.kanban_column ?? "").trim().toLowerCase()];
        if (!stage) return <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>;
        return (
          <span style={{ background: stage.bg, color: stage.color, border: `1px solid ${stage.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {stage.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Created</span>,
      dataIndex: "created_date", key: "created_date",
      render: (v, row) => (
        <span style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>
          {v || (row.created_at ? dayjs(row.created_at).format("DD-MM-YYYY") : "—")}
        </span>
      ),
    },
    {
      title: <span style={thStyle}>Action</span>,
      key: "action", align: "center",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
          <Tooltip title="Open lead">
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedLeadId(row.id); }}
              style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid #fde68a", background: "#fffbeb", color: "#d97706", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Open
            </button>
          </Tooltip>
          {isMyOwnLead(row) && (
            <Tooltip title="You created this lead — take it into your Inbox">
              <button
                onClick={(e) => { e.stopPropagation(); handleAssignToMe(row); }}
                disabled={allocateLeads.isPending}
                style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0891b2", fontSize: 12, fontWeight: 600, cursor: allocateLeads.isPending ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
              >
                Assign to me
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  /* Pick the assignee first, then tick the leads — same flow as All Leads */
  const handleAdminSelected = (user) => {
    setSelectedAdmin(user);
    setShowAllocateModal(false);
    setSelectedIds([]);
  };

  const handleConfirmAllocate = () => {
    if (!selectedAdmin) { toast.error("Please select an internal user first."); return; }
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "warning", title: "No leads selected", text: "Tick at least one lead to allocate.", confirmButtonColor: "#d97706" });
      return;
    }
    const limit = selectedAdmin.lead_limit ?? 0;
    const alreadyHas = selectedAdmin.total_assigned_leads ?? 0;
    if (limit > 0 && alreadyHas + selectedIds.length > limit) {
      const canTake = Math.max(0, limit - alreadyHas);
      Swal.fire({
        icon: "error",
        title: "Lead Limit Exceeded",
        html: `<p><strong>${selectedAdmin.name}</strong> has a lead limit of <strong>${limit}</strong>.</p>
               <p>Currently assigned: <strong>${alreadyHas}</strong> &nbsp;·&nbsp; You're trying to add: <strong>${selectedIds.length}</strong></p>
               <p>Can still accept: <strong style="color:${canTake > 0 ? "#16a34a" : "#dc2626"}">${canTake}</strong> more lead(s).</p>`,
        confirmButtonColor: "#d97706",
      });
      return;
    }
    Swal.fire({
      title: "Confirm Allocation",
      html: `Assign <strong>${selectedIds.length}</strong> lead(s) to <strong>${selectedAdmin.name}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Allocate",
      confirmButtonColor: "#d97706",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      allocateLeads.mutate(
        { adminId: selectedAdmin.id, leadIds: selectedIds },
        {
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: "Allocated!",
              text: `${selectedIds.length} lead(s) assigned to ${selectedAdmin.name}.`,
              confirmButtonColor: "#d97706",
            });
            // Allocated leads are no longer unassigned — clear and let the list refetch
            setSelectedIds([]);
            setSelectedAdmin(null);
          },
          onError: (err) =>
            Swal.fire("Error", err?.message || "Failed to allocate leads.", "error"),
        },
      );
    });
  };

  const handleApply = () => { setAppliedFilter({ ...filter }); setPage(1); };
  const handleClear = () => {
    setFilter({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setPage(1);
  };

  const activeFilterCount = [
    appliedFilter.start_date,
    appliedFilter.end_date,
  ].filter(Boolean).length;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)", borderRadius: 16, padding: "22px 26px", marginBottom: 20, boxShadow: "0 4px 18px rgba(217,119,6,0.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 46, height: 46, background: "rgba(255,255,255,0.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20 }}>
                <UserAddOutlined />
              </div>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontSize: 24, fontWeight: 700 }}>Unassigned Leads</h4>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 2 }}>
                  Leads waiting to be picked up — nobody owns these yet
                </div>
              </div>
            </div>
          </div>

          {/* ── Filters ── */}
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 18, overflow: "hidden" }}>
            <div
              onClick={() => setFiltersOpen((v) => !v)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "#fffbeb", borderBottom: filtersOpen ? "1px solid #fde68a" : "none" }}
            >
              <span style={{ fontWeight: 700, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                <FilterOutlined /> Filters
                {activeFilterCount > 0 && (
                  <span style={{ background: "#d97706", color: "#fff", borderRadius: 20, padding: "1px 9px", fontSize: 11 }}>
                    {activeFilterCount}
                  </span>
                )}
              </span>
              <span style={{ color: "#92400e", fontSize: 13 }}>{filtersOpen ? "▲ Collapse" : "▼ Expand"}</span>
            </div>

            {filtersOpen && (
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Search name, email, phone..."
                    value={filter.search}
                    onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                    style={{ borderRadius: 9, height: 40 }}
                    allowClear
                  />
                  <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    value={filter.start_date ? dayjs(filter.start_date) : null}
                    onChange={(_, ds) => setFilter((f) => ({ ...f, start_date: ds ? dayjs(ds, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                  <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    value={filter.end_date ? dayjs(filter.end_date) : null}
                    onChange={(_, ds) => setFilter((f) => ({ ...f, end_date: ds ? dayjs(ds, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    onClick={handleClear}
                    style={{ padding: "8px 18px", borderRadius: 9, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <ClearOutlined /> Clear
                  </button>
                  <button
                    onClick={handleApply}
                    style={{ padding: "8px 22px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Table ── */}
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, background: "linear-gradient(135deg,#f59e0b,#d97706)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 17 }}>
                <UserAddOutlined />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1f2937" }}>Unassigned Leads</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {total} lead{total !== 1 ? "s" : ""} with no owner
                </div>
              </div>

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {selectedAdmin && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                    <TeamOutlined /> {selectedAdmin.name}
                    <span style={{ background: "#fef3c7", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                      {selectedAdmin.total_assigned_leads ?? 0} / {selectedAdmin.lead_limit ?? 0}
                    </span>
                    <button
                      onClick={() => { setSelectedAdmin(null); setSelectedIds([]); }}
                      title="Cancel allocation"
                      style={{ border: "none", background: "none", color: "#b45309", cursor: "pointer", fontWeight: 700, padding: 0, lineHeight: 1 }}
                    >
                      ✕
                    </button>
                  </span>
                )}
                <button
                  onClick={() => setImportOpen((o) => !o)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: "#d97706", border: "1px solid #f59e0b", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", outline: importOpen ? "2px solid #f59e0b" : "none", outlineOffset: 2 }}
                >
                  <UploadOutlined /> Import
                </button>
                <button
                  onClick={selectedAdmin ? handleConfirmAllocate : () => setShowAllocateModal(true)}
                  disabled={allocateLeads.isPending}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", borderRadius: 10, padding: "8px 18px", fontWeight: 600, fontSize: 13, border: "none", cursor: allocateLeads.isPending ? "not-allowed" : "pointer", opacity: allocateLeads.isPending ? 0.7 : 1 }}
                >
                  <TeamOutlined />
                  {allocateLeads.isPending
                    ? "Allocating…"
                    : selectedAdmin
                      ? `Confirm Allocate${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                      : "Allocate Leads"}
                </button>
              </div>
            </div>

            {importOpen && (
              <div style={{ padding: "14px 24px", background: "#fffdf7", borderBottom: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>Import leads (.xlsx):</span>
                <input type="file" onChange={handleFileChange} className={`form-control ${fileError ? "is-invalid" : ""}`} style={{ borderRadius: 8, maxWidth: 320 }} />
                <button
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: importMutation.isPending ? "not-allowed" : "pointer", opacity: importMutation.isPending ? 0.7 : 1 }}
                >
                  {importMutation.isPending ? "Uploading…" : "Upload"}
                </button>
                <a
                  href="/assets/images/import_enquiry_format_trademark.xlsx"
                  download
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: "#d97706", border: "1px solid #f59e0b", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 13, textDecoration: "none" }}
                >
                  <DownloadOutlined /> Format
                </a>
                {fileError && <span style={{ color: "#dc2626", fontSize: 12, fontWeight: 600 }}>{fileError}</span>}
                <span style={{ fontSize: 11, color: "#6b7280", flexBasis: "100%" }}>
                  Required columns: <b>name</b>, <b>phone</b> (without country code), <b>country_code</b> (e.g. +91), <b>email</b>.
                </span>
              </div>
            )}

            {selectedAdmin && (
              <div style={{ padding: "10px 24px", background: "#fffbeb", borderBottom: "1px solid #fde68a", color: "#92400e", fontSize: 13 }}>
                <strong>Allocate mode:</strong>&nbsp;tick the leads to assign to <strong>{selectedAdmin.name}</strong>, then click <strong>Confirm Allocate</strong>.
              </div>
            )}

            {isFetching ? (
              <div style={{ padding: 24 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 14 }} />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No unassigned leads — everything has an owner</span>} />
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  dataSource={leads}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  // Checkboxes appear only after an assignee is picked
                  rowSelection={
                    selectedAdmin
                      ? {
                          selectedRowKeys: selectedIds,
                          onChange: (keys) => setSelectedIds(keys),
                        }
                      : undefined
                  }
                  onRow={(row) => ({
                    style: { cursor: "pointer" },
                    onClick: () => {
                      // In allocate mode a row click toggles the tick instead of
                      // opening the lead — otherwise ticking is fiddly.
                      if (selectedAdmin) {
                        setSelectedIds((prev) =>
                          prev.includes(row.id)
                            ? prev.filter((id) => id !== row.id)
                            : [...prev, row.id],
                        );
                        return;
                      }
                      setSelectedLeadId(row.id);
                    },
                  })}
                />
              </div>
            )}

            {/* Infinite scroll sentinel — crossing it pulls the next page. */}
            {leads.length > 0 && (
              <div ref={sentinelRef} style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", background: "#fffdf7" }}>
                {hasMore ? (
                  <span style={{ fontSize: 13, color: "#b45309", fontWeight: 600 }}>
                    {isFetching || isLoadingMore ? "Loading more leads…" : "Scroll for more"}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    All {leads.length} unassigned lead{leads.length !== 1 ? "s" : ""} loaded
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Pick who to allocate to ── */}
      {showAllocateModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowAllocateModal(false); }}
          style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 580, overflow: "hidden", boxShadow: "0 20px 60px rgba(217,119,6,.25)", border: "2px solid #fde68a" }}>
            <div style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, background: "rgba(255,255,255,.2)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>
                  <TeamOutlined />
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Allocate Leads</div>
                  <div style={{ color: "rgba(255,255,255,.8)", fontSize: 11 }}>Select an internal user to assign leads to</div>
                </div>
              </div>
              <button
                onClick={() => setShowAllocateModal(false)}
                style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "#fff", cursor: "pointer", fontSize: 14 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "20px 22px", maxHeight: "60vh", overflowY: "auto" }}>
              {internalUsers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                  <TeamOutlined style={{ fontSize: 40, marginBottom: 10, display: "block" }} />
                  <div>No internal users found</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {internalUsers.map((user) => {
                    const used = user.total_assigned_leads ?? 0;
                    const limit = user.lead_limit ?? 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const overLimit = limit > 0 && used >= limit;
                    return (
                      <div
                        key={user.id}
                        onClick={() => { if (!overLimit) handleAdminSelected(user); }}
                        title={overLimit ? `${user.name} has reached their lead limit (${limit})` : ""}
                        style={{
                          border: `1.5px solid ${overLimit ? "#fecaca" : "#fde68a"}`,
                          background: overLimit ? "#fff5f5" : "#fffdf7",
                          borderRadius: 12, padding: "12px 14px",
                          cursor: overLimit ? "not-allowed" : "pointer",
                          opacity: overLimit ? 0.7 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {user.name?.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{user.name}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ background: overLimit ? "#fef2f2" : "#fffbeb", color: overLimit ? "#dc2626" : "#b45309", border: `1px solid ${overLimit ? "#fecaca" : "#fde68a"}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                              {used} / {limit} leads
                            </span>
                            {overLimit && (
                              <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3, fontWeight: 600 }}>Limit reached</div>
                            )}
                          </div>
                        </div>
                        <div style={{ background: "#f3f4f6", borderRadius: 99, height: 6, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 99, width: `${pct}%`, background: pct >= 100 ? "linear-gradient(90deg,#ef4444,#dc2626)" : pct >= 80 ? "linear-gradient(90deg,#f59e0b,#d97706)" : "linear-gradient(90deg,#fbbf24,#d97706)", transition: "width .4s ease" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
                          <span>Role: <strong>{user.role}</strong></span>
                          <span>{pct}% used &nbsp;·&nbsp; {Math.max(0, limit - used)} remaining</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", background: "#fafafa", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowAllocateModal(false)}
                style={{ padding: "8px 22px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lead activity panel (same as every other lead screen) ── */}
      {followUpEnquiry && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f1f5f9", animation: "slideInRight .28s ease" }}>
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => { setFollowUpEnquiry(null); setSelectedLeadId(null); }}
          />
        </div>
      )}
    </div>
  );
}
